import {CatalogConfig} from '../types'

// Member profile catalogs — Industries and Professional Interests. Both are backed by
// api/models.py {IndustryCatalog, ProfessionalInterestCatalog}. The plain read endpoints
// under /catalog/{industries,professional-interests}/ stay unchanged (member profile forms
// depend on them); the admin CRUD lives under /catalog/admin/ so the two surfaces can
// evolve independently.

export const industriesConfig: CatalogConfig = {
  slug: 'industries',
  endpoint: '/catalog/admin/industries/',
  titleId: 'CATALOG.INDUSTRIES.TITLE',
  singularId: 'CATALOG.INDUSTRIES.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 255,
    },
  ],
}

export const interestsConfig: CatalogConfig = {
  slug: 'professional-interests',
  endpoint: '/catalog/admin/professional-interests/',
  titleId: 'CATALOG.INTERESTS.TITLE',
  singularId: 'CATALOG.INTERESTS.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 255,
    },
  ],
}

export const hobbiesConfig: CatalogConfig = {
  slug: 'hobbies',
  endpoint: '/catalog/admin/hobbies/',
  titleId: 'CATALOG.HOBBIES.TITLE',
  singularId: 'CATALOG.HOBBIES.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 255,
    },
  ],
}

export const clubsConfig: CatalogConfig = {
  slug: 'clubs',
  endpoint: '/catalog/admin/clubs/',
  titleId: 'CATALOG.CLUBS.TITLE',
  singularId: 'CATALOG.CLUBS.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name', 'city'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 255,
    },
    {
      name: 'city',
      labelId: 'CATALOG.FIELD.CITY',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 255,
    },
  ],
}

export const personalInterestsConfig: CatalogConfig = {
  slug: 'personal-interests',
  endpoint: '/catalog/admin/personal-interests/',
  titleId: 'CATALOG.PERSONAL_INTERESTS.TITLE',
  singularId: 'CATALOG.PERSONAL_INTERESTS.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 255,
    },
  ],
}

export const rateExpertiseConfig: CatalogConfig = {
  slug: 'rate-expertise',
  endpoint: '/catalog/admin/rate-expertise/',
  titleId: 'CATALOG.RATE_EXPERTISE.TITLE',
  singularId: 'CATALOG.RATE_EXPERTISE.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 50,
    },
  ],
}

// Relationship catalog — backs the "relationship" field in a member's Relatives (spouse,
// parent, child, sibling…). The read endpoint `/relatives/relationships/` stays for
// selectors; admin CRUD lives at /catalog/admin/relationships/.
export const relationshipsConfig: CatalogConfig = {
  slug: 'relationships',
  endpoint: '/catalog/admin/relationships/',
  titleId: 'CATALOG.RELATIONSHIPS.TITLE',
  singularId: 'CATALOG.RELATIONSHIPS.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 100,
    },
    {
      name: 'description',
      labelId: 'CATALOG.FIELD.DESCRIPTION',
      type: 'text',
      showInTable: false,
      required: false,
    },
  ],
}

// MemberCategory lives under /taxonomy/categories/ (existing DefaultRouter — not the
// /catalog/admin/* prefix). Same admin shape: name/order/is_active + reorder action.
export const memberCategoryConfig: CatalogConfig = {
  slug: 'member-category',
  endpoint: '/taxonomy/categories/',
  titleId: 'CATALOG.MEMBER_CATEGORY.TITLE',
  singularId: 'CATALOG.MEMBER_CATEGORY.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {name: 'order', labelId: 'CATALOG.FIELD.ORDER', type: 'text', showInTable: true},
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 100,
    },
    {
      name: 'description',
      labelId: 'CATALOG.FIELD.DESCRIPTION',
      type: 'text',
      showInTable: false,
      required: false,
    },
  ],
}

export const memberCatalogConfigs: CatalogConfig[] = [
  industriesConfig,
  interestsConfig,
  hobbiesConfig,
  clubsConfig,
  personalInterestsConfig,
  rateExpertiseConfig,
  memberCategoryConfig,
  relationshipsConfig,
]
